import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CalendrierIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Link } from 'react-router-dom';

import { enregistrerDistancePreparee, lireProfilAthlete, type AthleteRattache, type ProfilAthlete } from '../Auth/moi.ts';
import { DISTANCES } from '../Construction/distanceCourse.ts';
import { aujourdhuiDans } from '../Planning/dates.ts';

function libelleJour(jour: string): string {
  return new Date(jour + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function libelleAjustement(secondes: number): string {
  if (!secondes) return 'aucun';
  const signe = secondes > 0 ? '+' : '−';
  const abs = Math.abs(secondes);
  const min = Math.floor(abs / 60);
  const s = abs % 60;
  return `${signe}${min ? `${min}′` : ''}${s ? `${String(s).padStart(min ? 2 : 1, '0')}″` : ''} par récupération`;
}

const Ligne = ({ libelle, children }: { libelle: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
    <Typography color="text.secondary" sx={{ width: 220, flexShrink: 0 }}>{libelle}</Typography>
    <Typography component="div">{children}</Typography>
  </Box>
);

/**
 * Apercu d'un athlete rattache (page Athletes) : la course qu'il prepare
 * (fixee ici par le coach), ses parametres d'allures, intervals.icu, son
 * fuseau horaire et l'acces a son calendrier.
 */
export default function ApercuAthlete({ athlete }: { athlete: AthleteRattache }) {
  const sso = athlete.ssoUserId ?? null;
  const [profil, setProfil] = React.useState<ProfilAthlete | null>(null);
  const [erreur, setErreur] = React.useState<string | null>(null);
  // Course preparee : enregistree des qu'elle est choisie.
  const [distance, setDistance] = React.useState<string>(athlete.distancePreparee ?? '');
  const [etatDistance, setEtatDistance] = React.useState<'' | 'enregistrement' | 'enregistre' | 'erreur'>('');
  React.useEffect(() => setDistance(athlete.distancePreparee ?? ''), [athlete.distancePreparee]);

  const choisirDistance = (valeur: string) => {
    if (!sso) return;
    setDistance(valeur);
    setEtatDistance('enregistrement');
    enregistrerDistancePreparee(sso, valeur || null)
      .then(() => setEtatDistance('enregistre'))
      .catch((e: Error) => {
        setEtatDistance('erreur');
        setErreur(e.message);
      });
  };

  React.useEffect(() => {
    setProfil(null);
    setErreur(null);
    if (!sso) return;
    lireProfilAthlete(sso)
      .then(setProfil)
      .catch((e: Error) => setErreur(e.message));
  }, [sso]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {sso && (
          <Button
            variant="contained"
            startIcon={<CalendrierIcon />}
            component={Link}
            to={`/planning?athlete=${encodeURIComponent(sso)}`}
          >
            Ouvrir son calendrier
          </Button>
        )}
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>Paramètres de l'athlète indisponibles : {erreur}</Alert>}

      <Box sx={{ maxWidth: 640 }}>
        <Ligne libelle="Adresse">{athlete.email}</Ligne>
        <Ligne libelle="Course préparée">
          <TextField
            select
            size="small"
            variant="standard"
            value={distance}
            onChange={(e) => choisirDistance(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            disabled={!sso}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Non renseignée</MenuItem>
            {DISTANCES.map((d) => <MenuItem key={d.code} value={d.code}>{d.libelle}</MenuItem>)}
          </TextField>
          <Typography variant="body2" color={etatDistance === 'erreur' ? 'error' : 'text.secondary'}>
            {etatDistance === 'enregistrement' && 'Enregistrement…'}
            {etatDistance === 'enregistre' && 'Enregistré. '}
            {etatDistance === 'erreur' && 'Enregistrement impossible. '}
            {etatDistance !== 'enregistrement' && 'Pour ses modèles de séance, seuls les tiens destinés à cette course (ou à toutes distances) sont proposés.'}
          </Typography>
        </Ligne>
        <Ligne libelle="VMA">
          {profil ? (profil.vma ? `${profil.vma.toLocaleString('fr-FR')} km/h` : 'non renseignée') : '…'}
        </Ligne>
        <Ligne libelle="Allures">
          {profil ? (profil.allures?.personnalisee ? 'table personnalisée' : 'table par défaut') : '…'}
        </Ligne>
        <Ligne libelle="Ajustement des récupérations">
          {profil ? libelleAjustement(profil.ajustementRecup ?? 0) : '…'}
        </Ligne>
        <Ligne libelle="intervals.icu">
          {athlete.icuConfigure ? 'relié : les séances posées y sont envoyées' : 'non relié'}
        </Ligne>
        <Ligne libelle="Fuseau horaire">
          {athlete.fuseau || 'par défaut'}
          {!athlete.fuseauRenseigne && ' (non renseigné)'}
          {athlete.fuseau && (
            <Typography variant="body2" color="text.secondary">
              Aujourd'hui pour lui : {libelleJour(aujourdhuiDans(athlete.fuseau))}
            </Typography>
          )}
        </Ligne>
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        La VMA vient des records saisis par l'athlète ; allures et récupérations se règlent dans les onglets du même nom.
      </Typography>
    </Box>
  );
}
